import fetch from 'isomorphic-fetch'
import React, { useState, useEffect } from 'react'
import Client from 'shopify-buy'

import Context from '~/context/StoreContext'
import { productConfig, swap } from '~/config/builder'
import { navigate } from 'gatsby'

const client = Client.buildClient(
  {
    storefrontAccessToken: process.env.SHOPIFY_ACCESS_TOKEN,
    domain: `${process.env.SHOP_NAME}.myshopify.com`,
  },
  fetch
)

const ContextProvider = ({ children }) => {
  let initialStoreState = {
    client,
    loading: false,
    complete: false,
    checkout: { lineItems: [] },
    products: [],
    shop: {},
    hasGiftWrap: false,
    upsellProducts: [],
    currentBox: {
      variantId: null,
      data: null,
      config: null,
      slots: 0,
      building: false,
      filter: [],
      category: 'Cat_1',
      items: [],
      weight: 0,
      hasNote: false,
    },
  }

  const [store, updateStore] = useState(initialStoreState)
  let isRemoved = false

  useEffect(() => {
    const initializeCheckout = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== 'undefined'
      const existingCheckoutID = isBrowser
        ? localStorage.getItem('shopify_checkout_id')
        : null

      const setCheckoutInState = checkout => {
        if (isBrowser) {
          localStorage.setItem('shopify_checkout_id', checkout.id)
        }

        updateStore(prevState => {
          return { ...prevState, checkout }
        })
      }

      const createNewCheckout = () => store.client.checkout.create()
      const fetchCheckout = id => store.client.checkout.fetch(id)

      if (existingCheckoutID) {
        try {
          const checkout = await fetchCheckout(existingCheckoutID)
          // Make sure this cart hasn’t already been purchased.
          if (!isRemoved && !checkout.completedAt) {
            setCheckoutInState(checkout)
            return
          }
        } catch (e) {
          localStorage.setItem('shopify_checkout_id', null)
        }
      }

      const newCheckout = await createNewCheckout()
      if (!isRemoved) {
        setCheckoutInState(newCheckout)
      }
    }

    initializeCheckout()
  }, [store.client.checkout])

  useEffect(
    () => () => {
      isRemoved = true
    },
    []
  )

  const context = {
    store,
    addVariantToCart: (variantId, quantity, customAttributes) => {
      if (variantId === '' || !quantity) {
        console.error('Both a size and quantity are required.')
        return
      }

      updateStore(prevState => {
        return { ...prevState, loading: true }
      })

      const { checkout, client } = store

      const checkoutId = checkout.id
      const lineItemsToUpdate = [
        { variantId, quantity: parseInt(quantity, 10), customAttributes },
      ]

      return client.checkout
        .addLineItems(checkoutId, lineItemsToUpdate)
        .then(checkout => {
          updateStore(prevState => {
            return { ...prevState, checkout, loading: false }
          })
        })
    },
    removeLineItem: (client, checkoutID, lineItemID) => {
      updateStore(prevState => {
        return { ...prevState, loading: true }
      })

      return client.checkout
        .removeLineItems(checkoutID, [lineItemID])
        .then(res => {
          updateStore(prevState => {
            return { ...prevState, checkout: res, loading: false }
          })
        })
    },
    updateLineItem: (client, checkoutID, lineItemID, quantity) => {
      updateStore(prevState => {
        return { ...prevState, loading: true }
      })

      const lineItemsToUpdate = [
        { id: lineItemID, quantity: parseInt(quantity, 10) },
      ]

      return client.checkout
        .updateLineItems(checkoutID, lineItemsToUpdate)
        .then(res => {
          updateStore(prevState => {
            return { ...prevState, checkout: res, loading: false }
          })
        })
    },
    setCurrentBox: (variantId, data) => {
      updateStore(prevState => {
        const config = data && productConfig(data.handle)
        const category = config.limits[0].tag
        const filter = config.required.concat(category)
        let slots = 0
        for (let val of config.limits) {
          slots += val.limit
        }
        return {
          ...prevState,
          complete: false,
          currentBox: {
            variantId,
            data,
            building: true,
            items: [],
            weight: 0,
            progress: {},
            category,
            filter,
            config,
            slots,
            hasNote: false,
            finalStep: false,
          },
        }
      })
    },
    setBoxItemQuantity: (item, quantity, onComplete) => {
      updateStore(prevState => {
        let { items, progress, category, weight } = prevState.currentBox
        const currentQuantity = items.filter(i => i.title === item.title).length
        if (!progress[category]) progress[category] = 0
        if (currentQuantity > quantity) {
          const index = items.findIndex(i => i.title === item.title)
          items.splice(index, 1)
          progress[category]--
          weight = weight - item.weight
        } else if (currentQuantity < quantity) {
          items.push(item)
          progress[category]++
          weight = weight + item.weight
        }

        return {
          ...prevState,
          currentBox: {
            ...prevState.currentBox,
            items,
            progress,
            weight,
          },
        }
      })
    },
    setBoxCategory: category => {
      updateStore(prevState => {
        const { items, data, slots } = prevState.currentBox
        const config = data && productConfig(data.handle)

        if (config) {
          const itemIndex = config.limits.findIndex(
            limit => limit.tag === category
          )
          const currItem = config.limits[itemIndex]
          const complete = items.length >= slots
          const finalStep = itemIndex + 1 === config.limits.length
          const filter = config.required.concat(currItem.tag)

          console.log(finalStep, itemIndex, config.limits.length)

          return {
            ...prevState,
            complete,
            currentBox: {
              ...prevState.currentBox,
              category,
              filter,
              complete,
              finalStep,
            },
          }
        }

        return prevState
      })
    },
    updateBoxCategory: () => {
      updateStore(prevState => {
        const {
          items,
          data,
          category,
          slots,
          progress,
          variantId,
          hasNote,
          weight,
        } = prevState.currentBox
        const { products, hasGiftWrap } = prevState
        const config = data && productConfig(data.handle)

        if (config) {
          const itemIndex = config.limits.findIndex(
            limit => limit.tag === category
          )
          const currItem = config.limits[itemIndex]
          const nextItem = config.limits[itemIndex + 1]
          const complete = items.length >= slots
          const result =
            !complete && progress[category] >= currItem.limit
              ? nextItem.tag
              : currItem.tag
          const filter = config.required.concat(result)

          if (complete) {
            // Add finished box to cart
            const lineItems = items.map((item, i) => ({
              key: `${i + 1}`,
              value: item.title,
            }))

            // Swap variant if weight limit is exceeded
            const update = swap.find(i => i.find === variantId)
            if (update && update.weight <= weight) {
              context.addVariantToCart(update.replace, 1, lineItems)
            } else {
              context.addVariantToCart(variantId, 1, lineItems)
            }

            // Add custom note to cart
            if (hasNote) {
              const lineItems = [
                { key: 'To', value: hasNote.noteTo },
                { key: 'From', value: hasNote.noteFrom },
              ]
              context.addVariantToCart(hasNote.shopifyId, 1, lineItems)
            }

            // Add gift wrapping to cart
            if (hasGiftWrap) {
              context.addVariantToCart(hasGiftWrap.shopifyId, 1)
            }
          }

          navigate(complete ? `/personalise-gift` : `/build-your-own/${result}`)

          return {
            ...prevState,
            complete,
            currentBox: {
              ...prevState.currentBox,
              category: result,
              filter,
              complete,
            },
          }
        }

        return prevState
      })
    },
    toggleGiftWrapping: shopifyId => {
      updateStore(prevState => {
        return {
          ...prevState,
          hasGiftWrap: !prevState.hasGiftWrap ? { shopifyId } : false,
        }
      })
    },
    updateBoxNote: hasNote => {
      updateStore(prevState => {
        return {
          ...prevState,
          currentBox: {
            ...prevState.currentBox,
            hasNote,
          },
        }
      })
    },
    updateUpsell: (shopifyId, quantity) => {
      updateStore(prevState => {
        const { upsellProducts } = prevState
        upsellProducts[shopifyId] = quantity
        return {
          ...prevState,
          upsellProducts,
        }
      })
    },
    processUpsell: () => {
      updateStore(prevState => {
        return { ...prevState, loading: true }
      })

      const { checkout, client, upsellProducts } = store
      const checkoutId = checkout.id
      const lineItems = Object.keys(upsellProducts)
        .map(variantId => ({
          variantId,
          quantity: parseInt(upsellProducts[variantId], 10),
        }))
        .filter(line => line.variantId && line.quantity)

      return client.checkout
        .addLineItems(checkoutId, lineItems)
        .then(checkout => {
          updateStore(prevState => {
            return {
              ...prevState,
              checkout,
              loading: false,
              upsellProducts: {},
            }
          })
        })
    },
  }

  return <Context.Provider value={context}>{children}</Context.Provider>
}
export default ContextProvider
